import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { EditIcon } from '@/components/icons/Icon';
import CountryFlag from '@/components/CountryFlag';
import WaitingSpinner from '@/components/WaitingSpinner';
import { ProjectSummary } from '@/api/ProjectSummary';
import { projectService } from '@/service/ProjectService';
import { partnerService } from '@/service/PartnerService';
import { countries } from '@/data/Countries';
import { formatOverview } from '@/data/DateTimeUtil';
import { AmountUtil } from '@/data/AmountUtil';
import { Box, DataList, HStack, SimpleGrid, Stack, Table, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import ExplanationPopover from '@/components/ExplanationPopover';
import Content from '@/components/Content';
import StatCard from '@/components/StatCard';
import ProjectDetailTabs from './ProjectDetailTabs';
import { useI18n } from '@/i18n/I18nContext';

interface ProjectDetailsViewProps {
    projectId: number;
    onEdit: () => void;
}

const valueOrDash = (value?: string | null): string => value && value.trim().length > 0 ? value : '-';

const ProjectDetailsView = ({ projectId, onEdit }: ProjectDetailsViewProps) => {
    const { t } = useI18n();
    const organization = useOrganization();
    const homeCurrency = organization?.currency;
    const [summary, setSummary] = useState<ProjectSummary | null>(null);
    const [partners, setPartners] = useState<Map<number, string>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    useEffect(() => {
        setIsLoading(true);
        setServerError('');
        Promise
            .all([projectService.find(projectId), partnerService.listAll()])
            .then(([projectSummary, partnerList]) => {
                setSummary(projectSummary);
                setPartners(new Map(partnerList.map(partner => [partner.id, partner.name])));
            })
            .catch(() => setServerError('Projekt konnte nicht geladen werden.'))
            .finally(() => setIsLoading(false));
    }, [projectId]);

    const totals = useMemo(() => ({
        budget: summary?.budget.reduce((total, yearlyBudget) => total + (yearlyBudget.budget ?? 0), 0) ?? 0,
        projectBudget: summary?.budget.reduce((total, yearlyBudget) => total + (yearlyBudget.projectBudget ?? 0), 0) ?? 0,
        fundraisingBudget: summary?.budget.reduce((total, yearlyBudget) => total + (yearlyBudget.fundraisingBudget ?? 0), 0) ?? 0,
    }), [summary]);

    if (isLoading || !organization) {
        return (
            <Stack py={8} align='center'>
                <WaitingSpinner size='xl' />
                <Text color='fg.muted'>Projekt wird geladen...</Text>
            </Stack>
        );
    }

    if (serverError) {
        return <Text color='red.500' py={6}>{serverError}</Text>;
    }

    if (!summary) {
        return null;
    }

    const project = summary.project;
    const partnerNames = summary.partnerIds
        .map(partnerId => partners.get(partnerId) ?? `Partner ${partnerId}`)
        .join(', ');

    return (
        <Content title={project.name} description={t('projects.details.description')}
            actions={<Button colorPalette='blue' variant='outline' onClick={onEdit}>
                <EditIcon /> {t('projects.details.edit')}
            </Button>}>
            <Box maxW='6xl' mx='auto' minW='0'>
                <ProjectDetailTabs projectId={projectId}>
                    <Stack gap='6' pt='4' minW='0'>
                        <SectionPanel title={
                            <HStack as='span' align='center' gap={3}>
                                <Box as='span' bg={project.color} boxSize='1rem' rounded='sm' borderWidth='1px' borderColor='gray.300' />
                                <span>{t('projects.sections.information')}</span>
                            </HStack>
                        }>
                            <DataList.Root orientation='horizontal' divideY='1px' gap={3}
                                css={{
                                    '& > div': { alignItems: 'start', flexDirection: { base: 'column', md: 'row' } },
                                    '& dt': { minWidth: { base: 0, md: '11rem' }, flexShrink: 0 },
                                    '& dd': { minWidth: 0, overflowWrap: 'anywhere' },
                                }}>
                                <DataList.Item>
                                    <DataList.ItemLabel>Partner Organisationen</DataList.ItemLabel>
                                    <DataList.ItemValue>{valueOrDash(partnerNames)}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Land</DataList.ItemLabel>
                                    <DataList.ItemValue>
                                        <HStack gap={2}>
                                            <CountryFlag code={project.country} />
                                            <Text>{countries[project.country] ?? project.country}</Text>
                                        </HStack>
                                    </DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Währung</DataList.ItemLabel>
                                    <DataList.ItemValue>{project.currency}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Budget</DataList.ItemLabel>
                                    <DataList.ItemValue>{project.endDate ? 'Fester Zeitraum' : 'Laufend'}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Anfangsdatum</DataList.ItemLabel>
                                    <DataList.ItemValue>{formatOverview(project.startDate)}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Enddatum</DataList.ItemLabel>
                                    <DataList.ItemValue>{project.endDate ? formatOverview(project.endDate) : '-'}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Interne Reserve</DataList.ItemLabel>
                                    <DataList.ItemValue>{project.internal ? 'Ja' : 'Nein'}&nbsp;<ExplanationPopover explanation='Interne Reserven werden von der Fundraising-Übersicht und dem Reporting ausgeschlossen.' /></DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Bemerkungen</DataList.ItemLabel>
                                    <DataList.ItemValue whiteSpace='pre-wrap'>{valueOrDash(project.comments)}</DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Erstellt</DataList.ItemLabel>
                                    <DataList.ItemValue>{formatOverview(project.createdAt)}</DataList.ItemValue>
                                </DataList.Item>
                            </DataList.Root>
                        </SectionPanel>

                        <SimpleGrid columns={{ base: 1, md: 3 }} gap='4'>
                            <StatCard label={t('projects.sections.totalBudget')} value={`${AmountUtil.format(totals.budget)} ${project.currency}`}
                                valueFontSize='xl' showAccentBorder={false} />
                            <StatCard label={t('projects.sections.projectBudget')} value={`${AmountUtil.format(totals.projectBudget)} ${homeCurrency}`}
                                valueFontSize='xl' showAccentBorder={false} />
                            <StatCard label={t('projects.sections.fundraisingBudget')} value={`${AmountUtil.format(totals.fundraisingBudget)} ${homeCurrency}`}
                                valueFontSize='xl' showAccentBorder={false} />
                        </SimpleGrid>

                        <SectionPanel title={t('projects.sections.yearlyBudgets')}>
                            {summary.budget.length > 0 ? (
                                <Box overflowX='auto'>
                                    <Table.Root variant='line' size='sm' minW='46rem'>
                                        <Table.Header>
                                            <Table.Row>
                                                <Table.ColumnHeader>Jahr</Table.ColumnHeader>
                                                <Table.ColumnHeader textAlign='right'>Budget</Table.ColumnHeader>
                                                <Table.ColumnHeader textAlign='right'>Kurs</Table.ColumnHeader>
                                                <Table.ColumnHeader textAlign='right'>Kursdatum</Table.ColumnHeader>
                                                <Table.ColumnHeader textAlign='right'>Projekt {homeCurrency}</Table.ColumnHeader>
                                                <Table.ColumnHeader textAlign='right'>Overhead</Table.ColumnHeader>
                                                <Table.ColumnHeader textAlign='right'>Fundraising {homeCurrency}</Table.ColumnHeader>
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>
                                            {summary.budget.map(yearlyBudget => (
                                                <Table.Row key={yearlyBudget.year}>
                                                    <Table.Cell fontWeight='bold'>{yearlyBudget.year}</Table.Cell>
                                                    <Table.Cell textAlign='right'>{AmountUtil.format(yearlyBudget.budget)} {project.currency}</Table.Cell>
                                                    <Table.Cell textAlign='right'>{AmountUtil.format(yearlyBudget.rate)}</Table.Cell>
                                                    <Table.Cell textAlign='right'>{formatOverview(yearlyBudget.rateDate)}</Table.Cell>
                                                    <Table.Cell textAlign='right'>{AmountUtil.format(yearlyBudget.projectBudget)} {homeCurrency}</Table.Cell>
                                                    <Table.Cell textAlign='right'>{AmountUtil.format(yearlyBudget.overhead, 1)}%</Table.Cell>
                                                    <Table.Cell textAlign='right'>{AmountUtil.format(yearlyBudget.fundraisingBudget)} {homeCurrency}</Table.Cell>
                                                </Table.Row>
                                            ))}
                                        </Table.Body>
                                    </Table.Root>
                                </Box>
                            ) : (
                                <Text color='fg.muted'>kein Budget eingetragen</Text>
                            )}
                        </SectionPanel>
                    </Stack>
                </ProjectDetailTabs>
            </Box>
        </Content>
    );
};

export default ProjectDetailsView;
